import React, { useState, useMemo, useEffect } from "react";
import { Spinner, Form, Button } from "react-bootstrap";
import { useEticketingBookingsQuery, useEticketingDashboardFiltersQuery } from "../../../../api/e-ticketing/e-ticketing.hooks";
import Pagination from "../../Pagination/Pagination";
import TicketsView from "./TicketsView";
import { useParams } from "react-router-dom";
import { getCurrentUser } from "../../PluginsMenu/Nestable/utils";

// Bookings Table Component
const BookingsTable = ({ onViewTickets, serviceId }) => {
  const { city_id } = getCurrentUser();
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 250;
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({
    category: "",
    status: "",
    route_id: "",
    payment: "",
    source: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const bookingQueryParams = useMemo(() => ({
    service_id: serviceId,
    ...(city_id ? { city_id } : {}),
    page: currentPage + 1,
    limit: itemsPerPage,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(filters.category ? { category: filters.category } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.route_id ? { route_id: filters.route_id } : {}),
    ...(filters.payment ? { paymentMethod: filters.payment } : {}),
    ...(filters.startDate ? { startDate: filters.startDate } : {}),
    ...(filters.endDate ? { endDate: filters.endDate } : {}),
    ...(filters.source ? { source: filters.source } : {}),
  }), [
    serviceId,
    city_id,
    currentPage,
    debouncedSearch,
    filters.category,
    filters.status,
    filters.route_id,
    filters.payment,
    filters.startDate,
    filters.endDate,
    filters.source,
  ]);

  const { data: bookingsResponse, isLoading } = useEticketingBookingsQuery(bookingQueryParams);

  const { data: dashboardFilters, isLoading: isLoadingFilters } = useEticketingDashboardFiltersQuery({
    ...(city_id ? { city_id } : {}),
    ...(serviceId ? { service_id: serviceId } : {}),
  });
  
  const bookings = bookingsResponse?.bookings || [];
  const pagination = bookingsResponse?.pagination || {};

  // Reset filters when serviceId changes to prevent stale selections
  useEffect(() => {
    setFilters({
      category: "",
      status: "",
      route_id: "",
      payment: "",
      source: "",
      startDate: "",
      endDate: "",
    });
    setSearchTerm("");
    setDebouncedSearch("");
    setCurrentPage(0);
  }, [serviceId]);

  // Get filter options from API
  const filterCategories = dashboardFilters?.filters?.category || [];
  const filterStatuses = dashboardFilters?.filters?.status || [];
  const filterServices = dashboardFilters?.filters?.service || [];
  const filterPaymentMethods = ["Cash", "Pos", "Online"];

  const totalItems = pagination.totalItems ?? pagination.total ?? bookings.length;
  const fallbackTotalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const totalPages = pagination.totalPages ?? pagination.pages ?? fallbackTotalPages;

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      status: "",
      route_id: "",
      payment: "",
      source: "",
      startDate: "",
      endDate: "",
    });
    setSearchTerm("");
    setDebouncedSearch("");
    setCurrentPage(0);
  };

  const hasActiveFilters = Object.values(filters).some(val => val !== "") || searchTerm !== "";

  return (
    <div className="col-12">
      <div className="card">
        {/* Header */}
        <div className="card-header">
          <h4 className="card-title m-0">Reservations</h4>
        </div>

        {/* Filters */}
        <div className="card-body border-bottom">
          <div className="row g-3">
            {/* Search */}
            <div className="col-md-12">
              <Form.Control
                type="text"
                placeholder="Search by booking ID, name, route or service"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(0);
                }}
              />
            </div>

            {/* Category Filter */}
            <div className="col-md-3">
              <Form.Select
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                disabled={isLoadingFilters}
              >
                <option value="">All Categories</option>
                {filterCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </Form.Select>
            </div>

            {/* Status Filter */}
            <div className="col-md-3">
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                disabled={isLoadingFilters}
              >
                <option value="">All Statuses</option>
                {filterStatuses.map(status => (
                  <option key={status} value={status.toLowerCase()}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Routes Filter */}
            <div className="col-md-3">
              <Form.Select
                value={filters.route_id}
                onChange={(e) => handleFilterChange("route_id", e.target.value)}
                disabled={isLoadingFilters}
              >
                <option value="">All Routes</option>
                {filterServices.map((service) => (
                  <option key={service.route_id} value={service.route_id}>
                    {service.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Payment Filter */}
            <div className="col-md-3">
              <Form.Select
                value={filters.payment}
                onChange={(e) => handleFilterChange("payment", e.target.value)}
                disabled={isLoadingFilters}
              >
                <option value="">All Payment Methods</option>
                {filterPaymentMethods.map(method => (
                  <option key={method} value={method.toLowerCase()}>
                    {method}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Start Date */}
            <div className="col-md-3">
              <Form.Control
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
                placeholder="Start Date"
              />
            </div>

            {/* End Date */}
            <div className="col-md-3">
              <Form.Control
                type="date"
                value={filters.endDate}
                min={filters.startDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
                placeholder="End Date"
                disabled={!filters.startDate}
              />
            </div>

            {/* Source Filter */}
            <div className="col-md-3">
              <Form.Select
                value={filters.source}
                onChange={(e) => handleFilterChange("source", e.target.value)}
                disabled={isLoadingFilters}
              >
                <option value="">All Sources</option>
                <option value="desktop">desktop</option>
                <option value="mobile">mobile</option>
                <option value="web">web</option>
              </Form.Select>
            </div>

            {/* Clear Filters Button */}
            <div className="col-md-3">
              <Button
                variant="outline-secondary"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="w-100"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center p-4">
              <Spinner animation="border" role="status" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Booking ID</th>
                      <th>Service</th>
                      <th>Category</th>
                      <th>Fare</th>
                      <th>Payment</th>
                      <th>Tickets</th>
                      <th>Booking Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length > 0 ? (
                      bookings.map((booking, i) => {
                        // Extract unique categories from tickets
                        const categoryList = [...new Set(booking.tickets?.map(t => t.type).filter(Boolean))];
                        const categories = categoryList.length > 2
                          ? `${categoryList.slice(0, 2).join(", ")} +${categoryList.length - 2} more`
                          : categoryList.join(", ");
                        // Calculate total fare (sum of all ticket prices)
                        const totalFare = booking.tickets?.reduce((sum, ticket) => sum + (ticket.price || 0), 0) || 0;

                        return (
                          <tr key={booking.booking_id}>
                            <td>{currentPage * itemsPerPage + i + 1}</td>
                            <td>{booking.booking_id}</td>
                            <td>{booking.route_name || "--"}</td>
                            <td>
                              <span
                                className="d-inline-block text-truncate align-middle"
                                style={{ maxWidth: "180px" }}
                                title={categoryList.join(", ")}
                              >
                                {categories || "--"}
                              </span>
                            </td>
                            <td>Rs. {totalFare.toLocaleString()}</td>
                            <td>{booking.paymentMethod || "--"}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span>{booking.tickets?.length || 0}</span>
                                {booking.tickets?.length > 0 && (
                                  <button
                                    className="btn btn-sm btn-link p-0"
                                    onClick={() => onViewTickets(booking)}
                                    title="View Tickets"
                                  >
                                    <i className="bi bi-eye" style={{ fontSize: "1.2rem" }}></i>
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                                <span>{booking.booking_status}</span>
                            </td>
                            
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No reservations found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalItems > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Reservations Component
const Reservations = () => {
  const [selectedBooking, setSelectedBooking] = useState(null);

  const { serviceId } = useParams();

  const handleViewTickets = (booking) => {
    setSelectedBooking(booking);
  };

  const handleBackToBookings = () => {
    setSelectedBooking(null);
  };

  return selectedBooking ? (
    <TicketsView booking={selectedBooking} onBack={handleBackToBookings} />
  ) : (
    <BookingsTable onViewTickets={handleViewTickets} serviceId={serviceId} />
  );
};

export default Reservations;
